/**
 * Care Team Hook
 *
 * Fetches healthcare professionals assigned to a specific cycle.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useApiClient } from "../../api/louise-api";
import { throwApiError } from "../../api/api-error";

// ============================================================================
// Types
// ============================================================================

export type CareTeamMember = {
  id: string;
  userId: string | null;
  name: string;
  role: string;
  email: string;
  phoneNumber: string | null;
  createdAt: string | null;
};

// ============================================================================
// Query Keys
// ============================================================================

export const careTeamKeys = {
  all: ["careTeam"] as const,
  byCycle: (tenantId: string, cycleId: string) =>
    [...careTeamKeys.all, "byCycle", tenantId, cycleId] as const,
};

// ============================================================================
// Hooks
// ============================================================================

/**
 * Hook to fetch healthcare professionals for a given cycle.
 */
export function useCareTeam(cycleId: string | undefined) {
  const { client, tenantId } = useApiClient();

  return useQuery<CareTeamMember[]>({
    queryKey: careTeamKeys.byCycle(tenantId ?? "", cycleId ?? ""),
    queryFn: async () => {
      const { data, error, response } = await client.GET(
        "/{tenantId}/cycles/{id}/cycle-healthcare-professionals",
        {
          params: {
            path: {
              tenantId,
              id: cycleId!,
            },
          },
        },
      );

      if (data) {
        return data.data ?? data;
      } else {
        throwApiError(
          error,
          "Failed to load care-team members",
          "useCareTeam",
          response?.status,
        );
      }
    },
    enabled: !!cycleId && !!tenantId,
  });
}

/**
 * Adds an official healthcare professional to a cycle. Patient-entered care
 * connections intentionally do not call this until they have been matched to
 * a healthcareProfessionalId by the provider directory/backend.
 */
export function useAddCareTeamMember() {
  const { client, tenantId } = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      cycleId,
      healthcareProfessionalId,
    }: {
      cycleId: string;
      healthcareProfessionalId: string;
    }) => {
      if (!tenantId) throw new Error("Tenant is required");
      const { data, error, response } = await client.POST(
        "/{tenantId}/cycles/{id}/cycle-healthcare-professionals",
        {
          params: { path: { tenantId, id: cycleId } },
          body: { cycleId, healthcareProfessionalId },
        },
      );
      if (data) return data.data;
      throwApiError(
        error,
        "We couldn’t add this care-team member.",
        "useAddCareTeamMember",
        response?.status,
      );
    },
    onSuccess: (_, { cycleId }) => {
      queryClient.invalidateQueries({
        queryKey: careTeamKeys.byCycle(tenantId ?? "", cycleId),
      });
    },
  });
}

/** Removes an official healthcare professional from a cycle. */
export function useRemoveCareTeamMember() {
  const { client, tenantId } = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      cycleId,
      healthcareProfessionalId,
    }: {
      cycleId: string;
      healthcareProfessionalId: string;
    }) => {
      if (!tenantId) throw new Error("Tenant is required");
      const { error, response } = await client.DELETE(
        "/{tenantId}/cycles/{id}/cycle-healthcare-professionals/{healthcareProfessionalId}",
        {
          params: {
            path: { tenantId, id: cycleId, healthcareProfessionalId },
          },
        },
      );
      if (error) {
        throwApiError(
          error,
          "We couldn’t remove this care-team member.",
          "useRemoveCareTeamMember",
          response?.status,
        );
      }
    },
    onSuccess: (_, { cycleId }) => {
      queryClient.invalidateQueries({
        queryKey: careTeamKeys.byCycle(tenantId ?? "", cycleId),
      });
    },
  });
}
